var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import { strings } from '../lib/localization';
import { FieldSelector } from './fields/field-selector';
import { execFormRules, setUsesHtmlProperty, qConsent, showConsentIfNeeded, } from '../lib/helpers';
import { useFileState } from '../hooks/use-file-input';
import { useOnLanguageChange } from '../hooks/use-language-change';
import { getValueOrDefault, getValuesOrDefault, } from '../lib/get-values-or-default';
import { getValidationErrorMessage } from '../lib/get-validation-error-message';
import { validateWithType } from '../lib/validate-with-type';
import { ConsentDialog } from './consent-dialog/consent-dialog';
export var FormRenderer = forwardRef(function (_a, ref) {
    var _b = _a.localizationFormatValues, localizationFormatValues = _b === void 0 ? {} : _b, props = __rest(_a, ["localizationFormatValues"]);
    useOnLanguageChange();
    var _c = useFileState({
        uploadFormFile: props.uploadFile,
    }), files = _c.files, addAndSendFiles = _c.addAndSendFiles, removeFile = _c.removeFile, hasAtleastOneUploadedFile = _c.hasAtleastOneUploadedFile;
    var formIdRef = useRef(props.formId);
    var valuesRef = useRef(props.values);
    var _d = useState(false), showConsentDialog = _d[0], setShowConsentDialog = _d[1];
    var makeForm = function (form) {
        return setUsesHtmlProperty(props.versionId, execFormRules(form, getValuesOrDefault(form, props.values)));
    };
    var _e = useState(makeForm(props.form)), form = _e[0], setForm = _e[1];
    // entire form is change from one to another
    if (formIdRef.current !== props.formId) {
        formIdRef.current = props.formId;
        setForm(setUsesHtmlProperty(props.versionId, execFormRules(props.form, getValuesOrDefault(form, props.values))));
    }
    var validateField = function (field, newValue) {
        if (field === null || field === void 0 ? void 0 : field.show) {
            var value = getValueOrDefault(field, props.values);
            newValue = newValue !== null && newValue !== void 0 ? newValue : value;
            var hasValue = !!newValue.value;
            if ((field.field_type === 'textfield' ||
                field.field_type === 'textarea') &&
                field.validationType &&
                hasValue &&
                !validateWithType(field.validationType, newValue.value)) {
                return [false, getValidationErrorMessage(field)];
            }
            else if (field.required && field.field_type === 'fileInput') {
                if (hasAtleastOneUploadedFile(field.qid)) {
                    field.errorMessage = undefined;
                }
                else {
                    return [
                        false,
                        field.requiredMessage || strings.formRequiredValidationError,
                    ];
                }
            }
            else if (field.required && !hasValue) {
                return [
                    false,
                    field.requiredMessage || strings.formRequiredValidationError,
                ];
            }
        }
        return [true, undefined];
    };
    var validateFieldAndGetNewForm = function (id, newValue) {
        var field = form.find(function (entry) { return entry.qid === id; });
        if (!field) {
            return form;
        }
        var _a = validateField(field, newValue), errorMessage = _a[1];
        return form.map(function (form) {
            if (form.qid === field.qid) {
                return __assign(__assign({}, form), { errorMessage: errorMessage });
            }
            return form;
        });
    };
    if (valuesRef.current !== props.values) {
        var _form = form;
        // Need to validate the form when props change since the consent field is added
        // dynamically and thus won't keep it's error state between changes
        if (valuesRef.current && valuesRef.current.qConsent) {
            _form = validateFieldAndGetNewForm(qConsent);
        }
        valuesRef.current = props.values;
        setForm(makeForm(_form));
    }
    useEffect(function () {
        if (props.languageCode) {
            strings.setLanguage(props.languageCode);
        }
    }, [props.languageCode]);
    useImperativeHandle(ref, function () { return ({
        submit: function () { return handleButtonClick('submitButton'); },
        validate: function () { return validateAndUpdateForm(); },
    }); });
    var handleValueChange = function (id, type, value) {
        var formEntry = form.find(function (entry) { return entry.qid === id; });
        if (formEntry === null || formEntry === void 0 ? void 0 : formEntry.errorMessage) {
            validateFieldAndUpdateForm(id, { type: type, value: value });
        }
        props.setFormValue(props.formId, id, {
            type: type,
            value: value,
        });
    };
    var handleBlur = function (id) {
        validateFieldAndUpdateForm(id);
    };
    var submit = function () {
        var values = getValuesOrDefault(form, props.values);
        var fileTokens = Object.entries(values)
            .filter(function (_a) {
            var val = _a[1];
            return val.type === 'fileInput';
        })
            .map(function (_a) {
            var val = _a[1];
            return val.tokens;
        })
            .reduce(function (acc, cur) { return __spreadArray(__spreadArray([], acc, true), cur, true); }, []);
        var filteredValues = Object.entries(values).reduce(function (acc, _a) {
            var _b;
            var key = _a[0], value = _a[1];
            var _form = form.find(function (fe) { return fe.qid === key; });
            if (_form && _form.show) {
                return __assign(__assign({}, acc), (_b = {}, _b[key] = value, _b));
            }
            else {
                return acc;
            }
        }, {});
        props.onSubmit(filteredValues, fileTokens);
    };
    var handleButtonClick = function (type) {
        if (type === 'submitButton') {
            if (validateAndUpdateForm()) {
                if (showConsentIfNeeded(form, props.consent.body)) {
                    setShowConsentDialog(true);
                }
                else {
                    submit();
                }
            }
        }
    };
    var onFileUploadComplete = function (id, tokens) {
        var values = getValuesOrDefault(form, props.values);
        var formValue = values[id];
        var newValue = __spreadArray(__spreadArray([], formValue.tokens, true), tokens, true);
        var newForm = form.map(function (entry) {
            if (entry.qid === id && entry.required) {
                return __assign(__assign({}, entry), { errorMessage: undefined });
            }
            else {
                return entry;
            }
        });
        setForm(newForm);
        props.setFormValue(props.formId, id, {
            type: formValue.type,
            tokens: newValue,
        });
    };
    var addFiles = function (id, files, limit) {
        var uploadPromises = addAndSendFiles(id, files, limit).uploadPromises;
        Promise.all(uploadPromises).then(function (tokens) {
            onFileUploadComplete(id, tokens);
        }, function () {
            // Do nothing, error is handled
        });
    };
    var _removeFile = function (id, position) {
        removeFile(id, position);
        var values = getValuesOrDefault(form, props.values);
        var formValue = values[id];
        var currentTokens = formValue.tokens;
        var newValue = currentTokens;
        if (currentTokens.length - 1 >= position) {
            newValue.splice(position, 1);
        }
        props.setFormValue(props.formId, id, {
            type: 'fileInput',
            tokens: newValue,
        });
    };
    var validateFieldAndUpdateForm = function (id, newValue) {
        setForm(validateFieldAndGetNewForm(id, newValue));
    };
    var isValid = function () {
        var hasAnyError = false;
        var newForm = form.map(function (formField) {
            var _a = validateField(formField), valid = _a[0], errorMessage = _a[1];
            formField.errorMessage = errorMessage;
            if (!valid) {
                hasAnyError = true;
            }
            return formField;
        });
        if (hasAnyError) {
            return [
                false,
                newForm.map(function (form) {
                    if (form.field_type === 'submitButton') {
                        form.errorMessage = strings.errorMessageBottom;
                    }
                    return form;
                }),
            ];
        }
        return [true, form];
    };
    var validateAndUpdateForm = function () {
        var _a = isValid(), valid = _a[0], form = _a[1];
        if (!valid) {
            setForm(form);
        }
        return valid;
    };
    var handleCloseConsentDialog = function () {
        setShowConsentDialog(false);
    };
    var handleApproveConsent = function () {
        if (validateAndUpdateForm()) {
            submit();
        }
        else {
            handleCloseConsentDialog();
        }
    };
    if (!form) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx(FieldSelector, { form: form, values: getValuesOrDefault(form, props.values), inputValueChange: handleValueChange, blurHandler: handleBlur, buttonClickHandler: handleButtonClick, localizationFormatValues: localizationFormatValues, files: files, addFiles: addFiles, removeFile: _removeFile, customFormField: props.customFormField, handleAlertLinkClick: props.handleAlertLinkClick }, void 0), showConsentDialog && (_jsx(ConsentDialog, { consent: props.consent, localizationFormatValues: localizationFormatValues, onApprove: handleApproveConsent, onClose: handleCloseConsentDialog }, void 0))] }, void 0));
});
